// src/components/dashboard/StudentsOverview.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  BookOpenIcon,
  PlusIcon,
  PaperAirplaneIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { getMyStudents, getStudentDetail } from '@/services/api';
import sessionService from '@/services/sessionService';

// ─── Helpers ────────────────────────────────────────────

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatTime(dateStr, i18n) {
  if (!dateStr) return '-';
  const locale = i18n?.language === 'nl' ? 'nl-NL' : 'en-US';
  return new Date(dateStr).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

// ─── Student Card (list view) ───────────────────────────

function StudentCard({ student, onClick, t, i18n }) {
  const attendanceRate = student.total_sessions > 0
    ? Math.round((student.attended_sessions / student.total_sessions) * 100)
    : null;

  const attendanceColor = attendanceRate === null
    ? 'text-slate-400'
    : attendanceRate >= 80 ? 'text-green-600' : attendanceRate >= 50 ? 'text-amber-600' : 'text-red-600';

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 border border-slate-200 overflow-hidden cursor-pointer group"
    >
      <div className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-bold text-slate-800 truncate">
                {student.display_name || student.email}
              </h3>
            </div>
            <p className="text-xs text-slate-500 truncate">{student.email}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(student.course_names || []).map((name, i) => (
                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">{name}</span>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 sm:flex-shrink-0 sm:ml-0">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 rounded-lg">
              <CheckCircleIcon className={`w-4 h-4 ${attendanceColor}`} />
              <span className="text-xs sm:text-sm text-slate-600">
                {attendanceRate !== null ? (
                  <><span className={`font-semibold ${attendanceColor}`}>{attendanceRate}%</span> <span className="hidden sm:inline">{t('students.attendance')}</span></>
                ) : (
                  <span className="text-slate-400">{t('students.noSessions')}</span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 rounded-lg">
              <DocumentTextIcon className="w-4 h-4 text-blue-500" />
              <span className="text-xs sm:text-sm text-slate-600">
                <span className="font-semibold">{student.report_count}</span> <span className="hidden sm:inline">{t('students.reports')}</span>
              </span>
            </div>
            {student.last_session_date && (
              <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 rounded-lg">
                <ClockIcon className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-500">{formatDate(student.last_session_date)}</span>
              </div>
            )}
            <div className="hidden sm:flex items-center">
              <ChevronLeftIcon className="w-5 h-5 text-slate-300 rotate-180 group-hover:text-cyan-500 transition-colors" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Inline Report Form (embedded, not modal) ──────────

function InlineReportForm({ sessionId, studentImId, onReportCreated, t }) {
  const [isOpen, setIsOpen] = useState(false);
  const [reportContent, setReportContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!reportContent.trim()) {
      setError(t('reports.form.errors.contentRequired'));
      return;
    }
    if (reportContent.length > 5000) {
      setError(t('reports.form.errors.contentTooLong'));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await sessionService.createReport(sessionId, {
        studentInstitutionMemberId: studentImId,
        reportContent: reportContent.trim(),
        homework: null,
        rating: null
      });
      if (result.success) {
        setIsOpen(false);
        setReportContent('');
        onReportCreated();
      } else {
        setError(result.message || t('reports.saveFailed'));
      }
    } catch {
      setError(t('reports.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 rounded-lg transition-colors border border-dashed border-cyan-300 hover:border-cyan-400"
      >
        <PlusIcon className="w-3.5 h-3.5" />
        {t('students.detail.writeReport')}
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
      <div>
        <textarea
          value={reportContent}
          onChange={(e) => setReportContent(e.target.value)}
          rows={3}
          maxLength={5000}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-vertical"
          placeholder={t('reports.form.contentPlaceholder')}
          disabled={isSubmitting}
          autoFocus
        />
        <p className="text-xs text-slate-400 mt-0.5">{reportContent.length}/5000</p>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          className="px-3 py-1.5 text-xs font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('reports.form.saving') : t('reports.form.createReport')}
        </button>
        <button
          type="button"
          onClick={() => { setIsOpen(false); setError(''); }}
          className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
          disabled={isSubmitting}
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}

// ─── Session Row ────────────────────────────────────────

function SessionRow({ session, studentImId, onReportCreated, t, i18n }) {
  const [expanded, setExpanded] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const formatSessionDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const handleSendEmail = async () => {
    if (!session.report?.report_id) return;
    setSendingEmail(true);
    try {
      const result = await sessionService.sendReportEmail(session.session_id, session.report.report_id);
      if (result.success) {
        onReportCreated();
      }
    } catch { /* silent */ }
    finally { setSendingEmail(false); }
  };

  const isEnded = session.session_status === 'ended';
  const isFuture = session.session_status === 'scheduled';

  return (
    <div className={`border rounded-lg overflow-hidden ${session.attended ? 'border-slate-200' : 'border-slate-200 bg-slate-50/50'}`}>
      {/* Session header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2.5 flex items-center justify-between text-left hover:bg-slate-50/80 transition-colors"
      >
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          {isFuture ? (
            <ClockIcon className="w-4 h-4 text-slate-300 flex-shrink-0" />
          ) : session.attended ? (
            <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
          ) : (
            <XCircleIcon className="w-4 h-4 text-red-400 flex-shrink-0" />
          )}

          <div className="min-w-0">
            <span className="text-sm font-medium text-slate-800 truncate block">
              {session.session_title}
              {session.session_number != null && <span className="text-slate-400 ml-1">#{session.session_number}</span>}
            </span>
            <span className="text-xs text-slate-400">
              {formatSessionDate(session.start_time)} {formatTime(session.start_time, i18n)} – {formatTime(session.end_time, i18n)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          {session.total_duration_seconds > 0 && (
            <span className="text-xs px-2 py-0.5 bg-slate-100 rounded text-slate-500">
              {formatDuration(session.total_duration_seconds)}
            </span>
          )}

          {session.report ? (
            <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded flex items-center gap-1">
              <DocumentTextIcon className="w-3 h-3" />
              {t('students.detail.hasReport')}
            </span>
          ) : isEnded ? (
            <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-600 rounded">
              {t('students.detail.noReport')}
            </span>
          ) : null}

          {expanded ? <ChevronDownIcon className="w-4 h-4 text-slate-400" /> : <ChevronRightIcon className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-3 pb-3 border-t border-slate-100">
          {/* Session times */}
          <div className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-1.5">
            <div className="text-xs">
              <span className="text-slate-400">{t('students.detail.plannedStart')}:</span>
              <span className="text-slate-700 font-medium ml-1">{formatTime(session.start_time, i18n)}</span>
            </div>
            <div className="text-xs">
              <span className="text-slate-400">{t('students.detail.plannedEnd')}:</span>
              <span className="text-slate-700 font-medium ml-1">{formatTime(session.end_time, i18n)}</span>
            </div>
            {session.actual_start_time && (
              <div className="text-xs">
                <span className="text-slate-400">{t('students.detail.actualStart')}:</span>
                <span className="text-green-700 font-medium ml-1">{formatTime(session.actual_start_time, i18n)}</span>
              </div>
            )}
            {session.actual_end_time && (
              <div className="text-xs">
                <span className="text-slate-400">{t('students.detail.actualEnd')}:</span>
                <span className="text-green-700 font-medium ml-1">{formatTime(session.actual_end_time, i18n)}</span>
              </div>
            )}
          </div>
          {/* Connection events timeline (new sessions) */}
          {session.connection_events && session.connection_events.length > 0 && (
            <div className="mt-2.5">
              <span className="text-xs text-slate-400 block mb-1.5">
                {t('students.detail.connections')}
                {session.connection_events.length > 1 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded">
                    {session.connection_events.length}x
                  </span>
                )}
              </span>
              <div className="space-y-1 ml-1">
                {session.connection_events.map((event, i) => (
                  <div key={event.id || i} className="flex items-center gap-1.5 text-xs">
                    <span className="font-mono text-slate-600 tabular-nums">{formatTime(event.joined_at, i18n)}</span>
                    <ArrowRightIcon className="w-2.5 h-2.5 text-slate-300 flex-shrink-0" />
                    <span className="font-mono text-slate-600 tabular-nums">
                      {event.left_at ? formatTime(event.left_at, i18n) : (
                        <span className="text-green-600 font-sans font-medium">{t('students.detail.stillConnected')}</span>
                      )}
                    </span>
                    {event.duration_seconds != null && (
                      <span className="text-slate-400">({formatDuration(event.duration_seconds)})</span>
                    )}
                  </div>
                ))}
              </div>
              {/* Summary row */}
              <div className="flex items-center gap-3 mt-2 pt-1.5 border-t border-slate-100">
                {session.total_duration_seconds > 0 && (
                  <span className="text-xs text-slate-500">
                    {t('students.detail.totalTime')}: <span className="font-medium text-slate-700">{formatDuration(session.total_duration_seconds)}</span>
                  </span>
                )}
                {session.breakout_duration_seconds > 0 && (
                  <span className="text-xs text-slate-500">
                    {t('students.detail.breakoutTime')}: <span className="font-medium text-slate-700">{formatDuration(session.breakout_duration_seconds)}</span>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Fallback for older sessions without connection_events */}
          {(!session.connection_events || session.connection_events.length === 0) && (session.attended || session.total_duration_seconds > 0) && (
            <div className="mt-2.5">
              <span className="text-xs text-slate-400 block mb-1.5">{t('students.detail.connections')}</span>
              <div className="flex items-center gap-1.5 text-xs ml-1">
                <span className="font-mono text-slate-600 tabular-nums">
                  {session.first_joined_at ? formatTime(session.first_joined_at, i18n) : session.joined_at ? formatTime(session.joined_at, i18n) : '-'}
                </span>
                <ArrowRightIcon className="w-2.5 h-2.5 text-slate-300 flex-shrink-0" />
                <span className="font-mono text-slate-600 tabular-nums">
                  {session.last_left_at ? formatTime(session.last_left_at, i18n) : session.left_at ? formatTime(session.left_at, i18n) : '-'}
                </span>
                {session.total_duration_seconds > 0 && (
                  <span className="text-slate-400">({formatDuration(session.total_duration_seconds)})</span>
                )}
              </div>
              {session.breakout_duration_seconds > 0 && (
                <div className="mt-1.5 text-xs text-slate-500 ml-1">
                  {t('students.detail.breakoutTime')}: <span className="font-medium text-slate-700">{formatDuration(session.breakout_duration_seconds)}</span>
                </div>
              )}
            </div>
          )}

          {/* Not attended message */}
          {!session.attended && isEnded && (
            <p className="mt-2.5 text-xs text-red-400 italic">{t('students.detail.didNotAttend')}</p>
          )}

          {/* Existing report */}
          {session.report && (
            <div className="mt-3 p-3 bg-white rounded-lg border border-slate-200">
              <div className="flex items-start justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <DocumentTextIcon className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-medium text-slate-500">{t('students.detail.reportBy')} {session.report.teacher_name}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {session.report.email_sent ? (
                    <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded flex items-center gap-0.5">
                      <CheckCircleIcon className="w-3 h-3" />
                      {t('students.detail.emailSent')}
                    </span>
                  ) : (
                    <button
                      onClick={handleSendEmail}
                      disabled={sendingEmail}
                      className="text-xs px-2 py-1 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 rounded transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      <PaperAirplaneIcon className="w-3 h-3" />
                      {sendingEmail ? t('reports.sending') : t('reports.sendEmail')}
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-line">{session.report.report_content}</p>
            </div>
          )}

          {/* Write report (only for ended sessions without a report) */}
          {!session.report && isEnded && (
            <InlineReportForm
              sessionId={session.session_id}
              studentImId={studentImId}
              onReportCreated={onReportCreated}
              t={t}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Course Section (expandable with sessions) ─────────

function CourseSection({ course, sessions, studentImId, onReportCreated, t, i18n }) {
  const [expanded, setExpanded] = useState(false);

  const attendanceRate = course.total_sessions > 0
    ? Math.round((course.attended_sessions / course.total_sessions) * 100)
    : null;
  const attendanceColor = attendanceRate === null
    ? 'text-slate-400'
    : attendanceRate >= 80 ? 'text-green-600' : attendanceRate >= 50 ? 'text-amber-600' : 'text-red-600';

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors text-left"
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <BookOpenIcon className="w-4 h-4 text-cyan-600" />
            <h4 className="font-semibold text-slate-800">{course.course_name}</h4>
          </div>
          <p className="text-xs text-slate-400 mt-0.5 ml-6">
            {t('students.detail.enrolledSince')} {formatShortDate(course.enrollment_date)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 rounded-lg">
            <CheckCircleIcon className={`w-4 h-4 ${attendanceColor}`} />
            <span className="text-xs text-slate-600">
              {attendanceRate !== null ? (
                <><span className={`font-semibold ${attendanceColor}`}>{attendanceRate}%</span> ({course.attended_sessions}/{course.total_sessions})</>
              ) : t('students.noSessions')}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 rounded-lg">
            <DocumentTextIcon className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-slate-600"><span className="font-semibold">{course.report_count}</span> {t('students.reports')}</span>
          </div>
          {expanded ? <ChevronDownIcon className="w-4 h-4 text-slate-400" /> : <ChevronRightIcon className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-2">
          {(!sessions || sessions.length === 0) ? (
            <p className="text-xs text-slate-400 py-2">{t('students.detail.noSessionsYet')}</p>
          ) : (
            sessions.map((session) => (
              <SessionRow
                key={session.session_id}
                session={session}
                studentImId={studentImId}
                onReportCreated={onReportCreated}
                t={t}
                i18n={i18n}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Student Detail View ────────────────────────────────

function StudentDetailView({ studentImId, onBack, t, i18n }) {
  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDetail = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError('');
    try {
      const result = await getStudentDetail(studentImId);
      if (result.success) {
        setDetail(result.data);
      } else {
        setError(result.message || t('students.detail.fetchError'));
      }
    } catch {
      setError(t('students.detail.fetchError'));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [studentImId, t]);

  const refreshDetail = useCallback(() => fetchDetail(true), [fetchDetail]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <button onClick={onBack} className="text-cyan-600 hover:text-cyan-700 underline">{t('students.detail.backToList')}</button>
      </div>
    );
  }

  if (!detail) return null;

  const { student, courses, sessionsByCourse = {} } = detail;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-slate-200 transition-colors text-slate-600">
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-slate-800 truncate">{student.display_name}</h2>
          <p className="text-sm text-slate-500 truncate">{student.email}</p>
        </div>
        <div className="flex items-center gap-2">
          {student.parent_email && (
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-green-50 rounded-lg" title={t('students.detail.parentEmailFilled')}>
              <CheckCircleIcon className="w-4 h-4 text-green-500" />
              <span className="text-xs font-medium text-green-700">{t('students.detail.parentEmailFilled')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Course sections */}
      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
        {t('students.detail.courseBreakdown')}
      </h3>
      <div className="space-y-3">
        {courses.length === 0 ? (
          <p className="text-slate-500 text-sm py-4">{t('students.detail.noCourses')}</p>
        ) : (
          courses.map((course) => (
            <CourseSection
              key={course.course_id}
              course={course}
              sessions={sessionsByCourse[course.course_id] || []}
              studentImId={studentImId}
              onReportCreated={refreshDetail}
              t={t}
              i18n={i18n}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Main StudentsOverview ──────────────────────────────

function StudentsOverview({ user, userRole, institutionName }) {
  const { t, i18n } = useTranslation();
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudentImId, setSelectedStudentImId] = useState(null);

  useEffect(() => {
    document.title = `${institutionName || t('students.defaultInstitution')} - ${t('students.title')}`;
  }, [institutionName, t]);

  const fetchStudents = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const result = await getMyStudents();
      if (result.success) {
        setStudents(result.data || []);
      } else {
        setError(result.message || t('students.fetchError'));
      }
    } catch {
      setError(t('students.fetchError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  const filteredStudents = students.filter((s) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (s.display_name || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q) ||
      (s.course_names || []).some(name => name.toLowerCase().includes(q))
    );
  });

  if (selectedStudentImId) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <StudentDetailView
          studentImId={selectedStudentImId}
          onBack={() => setSelectedStudentImId(null)}
          t={t}
          i18n={i18n}
        />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <UserGroupIcon className="w-7 h-7 text-cyan-600" />
            {t('students.title')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{t('students.subtitle')}</p>
          <p className="text-xs text-slate-400 mt-0.5">{t('students.disclaimer')}</p>
        </div>
        {students.length > 0 && (
          <div className="relative mt-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={t('students.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>
        )}
      </div>

      {isLoading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
        </div>
      )}

      {!isLoading && error && (
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">{error}</p>
          <button onClick={fetchStudents} className="text-cyan-600 hover:text-cyan-700 underline text-sm">{t('students.retry')}</button>
        </div>
      )}

      {!isLoading && !error && students.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <AcademicCapIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">{t('students.empty.title')}</h3>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">{t('students.empty.description')}</p>
        </div>
      )}

      {!isLoading && !error && students.length > 0 && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">
            {searchQuery.trim()
              ? t('students.showingFiltered', { shown: filteredStudents.length, total: students.length })
              : t('students.totalCount', { count: students.length })}
          </p>
        </div>
      )}

      {!isLoading && !error && (
        <div className="space-y-3">
          {filteredStudents.map((student) => (
            <StudentCard
              key={student.institution_member_id}
              student={student}
              onClick={() => setSelectedStudentImId(student.institution_member_id)}
              t={t}
              i18n={i18n}
            />
          ))}
          {filteredStudents.length === 0 && searchQuery.trim() && (
            <div className="text-center py-8">
              <p className="text-sm text-slate-500">{t('students.noSearchResults')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StudentsOverview;